import { createGlobalStyle } from "styled-components";

/*
 * DataTables (and the raw HTML this component injects via renderToString
 * for custom/action cells) live outside React's render tree once mounted —
 * jQuery manipulates that DOM directly. That means these styles cannot be
 * scoped to a React component the normal styled-components way; they have
 * to stay global, same as SideNavTooltipGlobalStyle. Render this once
 * inside <ControlDataGrid>.
 *
 * The --color-*, --space-*, --radius-* variables referenced below are
 * generated at runtime by globalStyle.ts from theme.ts — this file no
 * longer needs its own @import of theme/constant.css (that file is now
 * a deprecated, empty stub).
 */
export const ControlDataGridGlobalStyle = createGlobalStyle`
:root {
     --dt-border-rgb: var(--color-grey-rgb);
     --dt-row-selected: var(--color-accent-dark, #162242);
     --dt-row-selected-rgb: var(--color-accent-dark-rgb);
     --dt-row-selected-text: var(--color-white-solid, #ffffff);
     --dt-row-selected-link: var(--color-accent-light, #ea8522);
     --dt-row-stripe: var(--color-accent-dark, #162242);
     --dt-row-stripe-rgb: var(--color-accent-dark-rgb);
     --dt-row-hover: var(--color-black);
     --dt-column-ordering: var(--color-black);
     --dt-html-background: var(--color-white-solid, #ffffff);
     --dt-page-link-color: var(--color-accent-dark, #162242);
     --dt-font-color: var(--color-text);
}

:root[data-bs-theme="dark"] {
     --dt-row-hover: var(--color-white, #ffffff);
     --dt-row-stripe: var(--color-white, #ffffff);
     --dt-row-stripe-rgb: var(--color-white);
     --dt-column-ordering: var(--color-white, #ffffff);
}

.page-link.active,
.active > .page-link {
     background-color: var(--dt-page-link-color);
     border-color: var(--dt-page-link-color);
}

.page-link {
     color: var(--dt-font-color);
}

.dtfc-top-blocker,
.dtfc-bottom-blocker {
     display: none !important;
}

.table > thead > tr > th {
     padding-top: var(--space-card-padding);
     padding-bottom: var(--space-card-padding);
}

table {
     font-size: medium;
}

table.dataTable td {
     font-weight: normal;
}

table.dataTable td.dt-control {
     text-align: center;
     cursor: pointer;
}

table.dataTable td.dt-control:before {
     display: inline-block;
     box-sizing: border-box;
     content: "";
     border-top: 5px solid transparent;
     border-left: 10px solid rgba(var(--color-black), 0.5);
     border-bottom: 5px solid transparent;
     border-right: 0px solid transparent;
}

table.dataTable tr.dt-hasChild td.dt-control:before {
     border-top: 10px solid rgba(var(--color-black), 0.5);
     border-left: 5px solid transparent;
     border-bottom: 0px solid transparent;
     border-right: 5px solid transparent;
}

html.dark table.dataTable td.dt-control:before,
:root[data-bs-theme="dark"] table.dataTable td.dt-control:before,
:root[data-theme="dark"] table.dataTable td.dt-control:before {
     border-left-color: rgba(var(--color-white, #ffffff), 0.5);
}

html.dark table.dataTable tr.dt-hasChild td.dt-control:before,
:root[data-bs-theme="dark"] table.dataTable tr.dt-hasChild td.dt-control:before,
:root[data-theme="dark"] table.dataTable tr.dt-hasChild td.dt-control:before {
     border-top-color: rgba(var(--color-white, #ffffff), 0.5);
     border-left-color: transparent;
}

div.dt-scroll {
     width: 100%;
}

div.dt-scroll-body thead tr,
div.dt-scroll-body tfoot tr {
     height: 0;
}

div.dt-scroll-body thead tr th,
div.dt-scroll-body thead tr td,
div.dt-scroll-body tfoot tr th,
div.dt-scroll-body tfoot tr td {
     height: 0 !important;
     padding-top: 0px !important;
     padding-bottom: 0px !important;
     border-top-width: 0px !important;
     border-bottom-width: 1px;
}

div.dt-scroll-body thead tr th div.dt-scroll-sizing,
div.dt-scroll-body thead tr td div.dt-scroll-sizing,
div.dt-scroll-body tfoot tr th div.dt-scroll-sizing,
div.dt-scroll-body tfoot tr td div.dt-scroll-sizing {
     height: 0 !important;
     overflow: hidden !important;
}

table.dataTable thead > tr > th,
table.dataTable tbody > tr > td {
     color: var(--dt-font-color);
}

table.dataTable thead > tr > th:active,
table.dataTable thead > tr > td:active {
     outline: none;
}

table.dataTable thead > tr > th.dt-orderable-asc span.dt-column-order:before,
table.dataTable thead > tr > th.dt-ordering-asc span.dt-column-order:before,
table.dataTable thead > tr > td.dt-orderable-asc span.dt-column-order:before,
table.dataTable thead > tr > td.dt-ordering-asc span.dt-column-order:before {
     position: absolute;
     display: block;
     bottom: 50%;
     content: "▲";
     content: "▲"/"";
}

table.dataTable thead > tr > th.dt-orderable-desc span.dt-column-order:after,
table.dataTable thead > tr > th.dt-ordering-desc span.dt-column-order:after,
table.dataTable thead > tr > td.dt-orderable-desc span.dt-column-order:after,
table.dataTable thead > tr > td.dt-ordering-desc span.dt-column-order:after {
     position: absolute;
     display: block;
     top: 50%;
     content: "▼";
     content: "▼"/"";
}

table.dataTable thead > tr > th.dt-orderable-asc,
table.dataTable thead > tr > th.dt-orderable-desc,
table.dataTable thead > tr > th.dt-ordering-asc,
table.dataTable thead > tr > th.dt-ordering-desc,
table.dataTable thead > tr > td.dt-orderable-asc,
table.dataTable thead > tr > td.dt-orderable-desc,
table.dataTable thead > tr > td.dt-ordering-asc,
table.dataTable thead > tr > td.dt-ordering-desc {
     position: relative;
     padding-right: 30px;
}

table.dataTable thead > tr > th.dt-orderable-asc span.dt-column-order,
table.dataTable thead > tr > th.dt-orderable-desc span.dt-column-order,
table.dataTable thead > tr > th.dt-ordering-asc span.dt-column-order,
table.dataTable thead > tr > th.dt-ordering-desc span.dt-column-order,
table.dataTable thead > tr > td.dt-orderable-asc span.dt-column-order,
table.dataTable thead > tr > td.dt-orderable-desc span.dt-column-order,
table.dataTable thead > tr > td.dt-ordering-asc span.dt-column-order,
table.dataTable thead > tr > td.dt-ordering-desc span.dt-column-order {
     top: 0;
     bottom: 0;
}

table.dataTable thead > tr > th.dt-orderable-asc span.dt-column-order:before,
table.dataTable thead > tr > th.dt-orderable-asc span.dt-column-order:after,
table.dataTable thead > tr > th.dt-orderable-desc span.dt-column-order:before,
table.dataTable thead > tr > th.dt-orderable-desc span.dt-column-order:after,
table.dataTable thead > tr > th.dt-ordering-asc span.dt-column-order:before,
table.dataTable thead > tr > th.dt-ordering-asc span.dt-column-order:after,
table.dataTable thead > tr > th.dt-ordering-desc span.dt-column-order:before,
table.dataTable thead > tr > th.dt-ordering-desc span.dt-column-order:after,
table.dataTable thead > tr > td.dt-orderable-asc span.dt-column-order:before,
table.dataTable thead > tr > td.dt-orderable-asc span.dt-column-order:after,
table.dataTable thead > tr > td.dt-orderable-desc span.dt-column-order:before,
table.dataTable thead > tr > td.dt-orderable-desc span.dt-column-order:after,
table.dataTable thead > tr > td.dt-ordering-asc span.dt-column-order:before,
table.dataTable thead > tr > td.dt-ordering-asc span.dt-column-order:after,
table.dataTable thead > tr > td.dt-ordering-desc span.dt-column-order:before,
table.dataTable thead > tr > td.dt-ordering-desc span.dt-column-order:after {
     left: 0;
     opacity: 0.125;
     line-height: 12px;
     font-size: 0.8em;
}

table.dataTable thead > tr > th.dt-orderable-asc,
table.dataTable thead > tr > th.dt-orderable-desc,
table.dataTable thead > tr > td.dt-orderable-asc,
table.dataTable thead > tr > td.dt-orderable-desc {
     cursor: pointer;
}

table.dataTable thead > tr > th.dt-orderable-asc:hover,
table.dataTable thead > tr > th.dt-orderable-desc:hover,
table.dataTable thead > tr > td.dt-orderable-asc:hover,
table.dataTable thead > tr > td.dt-orderable-desc:hover {
     outline: 2px solid rgba(var(--color-black), 0.05);
     outline-offset: -2px;
}

table.dataTable thead > tr > th.dt-ordering-asc span.dt-column-order:before,
table.dataTable thead > tr > th.dt-ordering-desc span.dt-column-order:after,
table.dataTable thead > tr > td.dt-ordering-asc span.dt-column-order:before,
table.dataTable thead > tr > td.dt-ordering-desc span.dt-column-order:after {
     opacity: 0.6;
}

table.dataTable thead > tr > th.sorting_desc_disabled span.dt-column-order:after,
table.dataTable thead > tr > th.sorting_asc_disabled span.dt-column-order:before,
table.dataTable thead > tr > td.sorting_desc_disabled span.dt-column-order:after,
table.dataTable thead > tr > td.sorting_asc_disabled span.dt-column-order:before {
     display: none;
}

div.dt-scroll-body > table.dataTable > thead > tr > th,
div.dt-scroll-body > table.dataTable > thead > tr > td {
     overflow: hidden;
}

:root.dark table.dataTable thead > tr > th.dt-orderable-asc:hover,
:root.dark table.dataTable thead > tr > th.dt-orderable-desc:hover,
:root.dark table.dataTable thead > tr > td.dt-orderable-asc:hover,
:root.dark table.dataTable thead > tr > td.dt-orderable-desc:hover,
:root[data-bs-theme="dark"] table.dataTable thead > tr > th.dt-orderable-asc:hover,
:root[data-bs-theme="dark"] table.dataTable thead > tr > th.dt-orderable-desc:hover,
:root[data-bs-theme="dark"] table.dataTable thead > tr > td.dt-orderable-asc:hover,
:root[data-bs-theme="dark"] table.dataTable thead > tr > td.dt-orderable-desc:hover {
     outline: 2px solid rgba(var(--color-white, #ffffff), 0.05);
}

div.dt-processing {
     position: absolute;
     top: 50%;
     left: 50%;
     width: 200px;
     margin-left: -100px;
     margin-top: -22px;
     text-align: center;
     padding: 2px;
     z-index: 10;
}

div.dt-processing > div:last-child {
     position: relative;
     width: 80px;
     height: 15px;
     margin: 1em auto;
}

div.dt-processing > div:last-child > div {
     position: absolute;
     top: 0;
     width: 13px;
     height: 13px;
     border-radius: 50%;
     background: var(--dt-row-selected);
     animation-timing-function: cubic-bezier(0, 1, 1, 0);
}

div.dt-processing > div:last-child > div:nth-child(1) {
     left: 8px;
     animation: datatables-loader-1 0.6s infinite;
}

div.dt-processing > div:last-child > div:nth-child(2) {
     left: 8px;
     animation: datatables-loader-2 0.6s infinite;
}

div.dt-processing > div:last-child > div:nth-child(3) {
     left: 32px;
     animation: datatables-loader-2 0.6s infinite;
}

div.dt-processing > div:last-child > div:nth-child(4) {
     left: 56px;
     animation: datatables-loader-3 0.6s infinite;
}

@keyframes datatables-loader-1 {
     0% { transform: scale(0); }
     100% { transform: scale(1); }
}

@keyframes datatables-loader-3 {
     0% { transform: scale(1); }
     100% { transform: scale(0); }
}

@keyframes datatables-loader-2 {
     0% { transform: translate(0, 0); }
     100% { transform: translate(24px, 0); }
}

table.dataTable.nowrap th,
table.dataTable.nowrap td {
     white-space: nowrap;
}

table.dataTable th,
table.dataTable td {
     box-sizing: border-box;
}

table.dataTable th.dt-left,
table.dataTable td.dt-left {
     text-align: left;
}

table.dataTable th.dt-center,
table.dataTable td.dt-center {
     text-align: center;
}

table.dataTable th.dt-right,
table.dataTable td.dt-right {
     text-align: right;
}

table.dataTable th.dt-justify,
table.dataTable td.dt-justify {
     text-align: justify;
}

table.dataTable th.dt-nowrap,
table.dataTable td.dt-nowrap {
     white-space: nowrap;
}

table.dataTable th.dt-empty,
table.dataTable td.dt-empty {
     text-align: center;
     vertical-align: top;
}

table.dataTable thead th,
table.dataTable thead td,
table.dataTable tfoot th,
table.dataTable tfoot td {
     text-align: left;
}

table.dataTable thead th.dt-head-left,
table.dataTable thead td.dt-head-left,
table.dataTable tfoot th.dt-head-left,
table.dataTable tfoot td.dt-head-left {
     text-align: left;
}

table.dataTable thead th.dt-head-center,
table.dataTable thead td.dt-head-center,
table.dataTable tfoot th.dt-head-center,
table.dataTable tfoot td.dt-head-center {
     text-align: center;
}

table.dataTable thead th.dt-head-right,
table.dataTable thead td.dt-head-right,
table.dataTable tfoot th.dt-head-right,
table.dataTable tfoot td.dt-head-right {
     text-align: right;
}

table.dataTable thead th.dt-head-justify,
table.dataTable thead td.dt-head-justify,
table.dataTable tfoot th.dt-head-justify,
table.dataTable tfoot td.dt-head-justify {
     text-align: justify;
}

table.dataTable thead th.dt-head-nowrap,
table.dataTable thead td.dt-head-nowrap,
table.dataTable tfoot th.dt-head-nowrap,
table.dataTable tfoot td.dt-head-nowrap {
     white-space: nowrap;
}

table.dataTable tbody th.dt-body-left,
table.dataTable tbody td.dt-body-left {
     text-align: left;
}

table.dataTable tbody th.dt-body-center,
table.dataTable tbody td.dt-body-center {
     text-align: center;
}

table.dataTable tbody th.dt-body-right,
table.dataTable tbody td.dt-body-right {
     text-align: right;
}

table.dataTable tbody th.dt-body-justify,
table.dataTable tbody td.dt-body-justify {
     text-align: justify;
}

table.dataTable tbody th.dt-body-nowrap,
table.dataTable tbody td.dt-body-nowrap {
     white-space: nowrap;
}

table.table.dataTable {
     clear: both;
     margin-bottom: 0;
     max-width: none;
     border-spacing: 0;
}

table.table.dataTable.table-striped > tbody > tr:nth-of-type(2n + 1) > * {
     box-shadow: none;
}

table.table.dataTable > :not(caption) > * > * {
     background-color: var(--bs-table-bg);
}

table.table.dataTable > tbody > tr {
     background-color: transparent;
}

table.table.dataTable > tbody > tr.selected > * {
     box-shadow: inset 0 0 0 9999px var(--dt-row-selected) !important;
     color: var(--dt-row-selected-text) !important;
}

table.table.dataTable > tbody > tr.selected a {
     color: var(--color-text);
     color: var(--dt-row-selected-link);
}

table.table.dataTable > tbody > tr > td > .grid-action-button,
table.table.dataTable > tbody > tr > td > div > .grid-action-button {
     color: var(--dt-font-color);
     background-color: transparent;
     border: none;
}

table.table.dataTable > tbody > tr.selected > td > .grid-action-button,
table.table.dataTable > tbody > tr.selected > td > div > .grid-action-button {
     color: var(--dt-row-selected-text);
}

table.table.dataTable > tbody > tr > td > .grid-action-button:hover,
table.table.dataTable > tbody > tr > td > div > .grid-action-button:hover,
table.table.dataTable > tbody > tr.selected > td > .grid-action-button:hover,
table.table.dataTable > tbody > tr.selected > td > div > .grid-action-button:hover {
     transform: translateY(-2px);
}

table .table.dataTable.table-striped > tbody > tr:nth-of-type(2n + 1) > * {
     box-shadow: inset 0 0 0 9999px rgba(var(--dt-row-stripe-rgb), 0.05);
}

table.table.dataTable.table-striped > tbody > tr:nth-of-type(2n + 1).selected > * {
     box-shadow: inset 0 0 0 9999px rgba(var(--dt-row-selected-rgb), 0.95);
}

table.table.dataTable.table-hover > tbody > tr:hover > * {
     box-shadow: inset 0 0 0 9999px rgba(var(--dt-row-hover), 0.075);
}

table.table.dataTable.table-hover > tbody > tr.selected:hover > * {
     box-shadow: inset 0 0 0 9999px rgba(var(--dt-row-selected-rgb), 0.975);
}

div.dt-container div.dt-layout-start > *:not(:last-child) {
     margin-right: var(--space-card-padding);
}

div.dt-container div.dt-layout-end > *:not(:first-child) {
     margin-left: var(--space-card-padding);
}

div.dt-container div.dt-layout-full {
     width: 100%;
}

div.dt-container div.dt-layout-full > *:only-child {
     margin-left: auto;
     margin-right: auto;
}

div.dt-container {
     display: block !important;
     border: none;
     border-radius: var(--space-card-padding);
     border-width: 1px;
     border-color: rgba(var(--dt-border-rgb), 0.45);
     margin: var(--space-icon-gap);
     padding: var(--space-card-padding) 0 var(--space-icon-gap) 0;
}

div.dt-layout-full > div.dt-scroll {
     border-bottom: solid;
     border-bottom-width: 1px;
     border-bottom-color: var(--color-divider-subtle, #e5e7eb);
}

div.dt-container > div {
     margin: var(--space-card-padding);
}

@media screen and (max-width: 767px) {
     div.dt-container div.dt-layout-start > *:not(:last-child) {
          margin-right: 0;
     }

     div.dt-container div.dt-layout-end > *:not(:first-child) {
          margin-left: 0;
     }
}

div.dt-container div.dt-info {
     font-size: small;
     font-weight: normal;
}

div.dt-container div.dt-length label {
     padding-right: var(--space-icon-gap);
     font-size: small;
     font-weight: normal;
     text-align: left;
     white-space: nowrap;
}

div.dt-container div.dt-length select {
     width: auto;
     display: inline-block;
     margin-right: var(--space-tight-padding);
     color: var(--dt-font-color);
     height: 36px;
}

div.dt-container div.dt-search {
     text-align: right;
}

div.dt-container div.dt-search label {
     font-weight: normal;
     white-space: nowrap;
     text-align: left;
}

div.dt-container div.dt-search input {
     margin-left: var(--space-tight-padding);
     display: inline-block;
     width: auto;
}

div.dt-container div.dt-paging {
     margin: 0;
}

div.dt-container div.dt-paging ul.pagination {
     margin: var(--space-01) 0;
     flex-wrap: wrap;
}

div.dt-container div.dt-row {
     position: relative;
}

.dt-scroll-headInner {
     width: 100% !important;
}

div.dt-scroll-head table.dataTable {
     margin-bottom: 0 !important;
     width: 100% !important;
}

div.dt-scroll-body {
     border-bottom-color: var(--bs-border-color);
     border-bottom-width: var(--bs-border-width);
     border-bottom-style: none;
}

div.dt-scroll-body > table {
     border-top: none;
     margin-top: 0 !important;
     margin-bottom: 0 !important;
}

div.dt-scroll-body > table > tbody > tr:first-child {
     border-top-width: 0;
}

div.dt-scroll-body > table > thead > tr {
     border-width: 0 !important;
}

div.dt-scroll-body > table > tbody > tr:last-child > * {
     border-bottom: none;
}

div.dt-scroll-foot > .dt-scroll-footInner {
     box-sizing: content-box;
}

div.dt-scroll-foot > .dt-scroll-footInner > table {
     margin-top: 0 !important;
     border-top: none;
}

div.dt-scroll-foot > .dt-scroll-footInner > table > tfoot > tr:first-child {
     border-top-width: 0 !important;
}

@media screen and (max-width: 767px) {
     div.dt-container div.dt-length,
     div.dt-container div.dt-search,
     div.dt-container div.dt-info,
     div.dt-container div.dt-paging {
          text-align: center;
     }

     div.dt-container .row {
          --bs-gutter-y: 0.5rem;
     }

     div.dt-container div.dt-paging ul.pagination {
          justify-content: center !important;
     }
}

table.dataTable.table-sm > thead > tr th.dt-orderable-asc,
table.dataTable.table-sm > thead > tr th.dt-orderable-desc,
table.dataTable.table-sm > thead > tr th.dt-ordering-asc,
table.dataTable.table-sm > thead > tr th.dt-ordering-desc,
table.dataTable.table-sm > thead > tr td.dt-orderable-asc,
table.dataTable.table-sm > thead > tr td.dt-orderable-desc,
table.dataTable.table-sm > thead > tr td.dt-ordering-asc,
table.dataTable.table-sm > thead > tr td.dt-ordering-desc {
     padding-right: 20px;
}

table.dataTable.table-sm > thead > tr th.dt-orderable-asc span.dt-column-order,
table.dataTable.table-sm > thead > tr th.dt-orderable-desc span.dt-column-order,
table.dataTable.table-sm > thead > tr th.dt-ordering-asc span.dt-column-order,
table.dataTable.table-sm > thead > tr th.dt-ordering-desc span.dt-column-order,
table.dataTable.table-sm > thead > tr td.dt-orderable-asc span.dt-column-order,
table.dataTable.table-sm > thead > tr td.dt-orderable-desc span.dt-column-order,
table.dataTable.table-sm > thead > tr td.dt-ordering-asc span.dt-column-order,
table.dataTable.table-sm > thead > tr td.dt-ordering-desc span.dt-column-order {
     right: 5px;
}

div.dt-scroll-head table.table-bordered {
     border-bottom-width: 0;
}

div.table-responsive > div.dt-container > div.row {
     margin: 0;
}

div.table-responsive > div.dt-container > div.row > div[class^="col-"]:first-child {
     padding-left: 0;
}

div.table-responsive > div.dt-container > div.row > div[class^="col-"]:last-child {
     padding-right: 0;
}

.sub-table-container {
     overflow-x: hidden;
}

.no-color {
     --bs-btn-bg: none !important;
     --bs-btn-color: var(--color-grey, #6c757d) !important;
}

table.dataTable.dtr-inline.collapsed > tbody > tr > td.dtr-control:before,
table.dataTable.dtr-inline.collapsed > tbody > tr > th.dtr-control:before {
     content: none !important;
}

.vfx-datagrid-resource {
     --vfx-resource-primary: var(--color-accent-dark, #162242);
     --vfx-resource-border: var(--color-divider-subtle, #e5e7eb);
     --vfx-resource-muted: var(--color-text-secondary, #687087);
     --vfx-resource-offwhite: var(--color-background-light, #f8f9fa);
     color: var(--vfx-resource-primary);
}

.vfx-datagrid-resource-header {
     align-items: flex-start;
     display: flex;
     gap: var(--space-card-padding);
     justify-content: space-between;
     margin-bottom: var(--space-card-padding);
}

.vfx-datagrid-resource-title {
     color: var(--vfx-resource-primary);
     font-size: 28px;
     font-weight: 700;
     line-height: 1.2;
     margin: 0;
}

.vfx-datagrid-resource-subtitle {
     color: var(--vfx-resource-muted);
     font-size: 14px;
     margin: var(--space-icon-gap) 0 0;
}

.vfx-datagrid-resource-primary-action {
     align-items: center;
     background: var(--vfx-resource-primary);
     border: 1px solid var(--vfx-resource-primary);
     border-radius: var(--radius-sm, 8px);
     color: var(--color-white-solid, #ffffff);
     display: inline-flex;
     font-weight: 600;
     gap: var(--space-tight-padding);
     min-height: 40px;
     padding: 0 var(--space-card-padding);
     transition: background-color 0.15s ease, border-color 0.15s ease, transform 0.1s ease;
}

.vfx-datagrid-resource-primary-action:hover,
.vfx-datagrid-resource-primary-action:focus-visible {
     background: var(--color-accent-light, #ea8522);
     border-color: var(--color-accent-light, #ea8522);
     color: var(--color-white-solid, #ffffff);
}

.vfx-datagrid-resource-primary-action:focus-visible {
     outline: none;
     box-shadow: var(--shadow-button-focus, 0 0 0px 2px #fff, 0 0 3px 4px rgba(234, 133, 34, 0.5));
}

.vfx-datagrid-resource-primary-action:active {
     transform: scale(0.98);
}

.vfx-datagrid-resource-card {
     background: var(--color-background, #ffffff);
     border: 1px solid var(--vfx-resource-border);
     border-radius: var(--radius-sm, 8px);
     overflow: hidden;
}

.vfx-datagrid-resource-child .vfx-datagrid-resource-card {
     box-shadow: none;
}

.vfx-datagrid-resource-child {
     margin-left: 0;
}

.vfx-datagrid-resource-toolbar {
     align-items: end;
     border-bottom: 1px solid var(--vfx-resource-border);
     display: flex;
     flex-wrap: wrap;
     gap: var(--space-card-padding);
     padding: var(--space-section-gap);
}

.vfx-datagrid-resource-search {
     flex: 1 1 260px;
     position: relative;
}

.vfx-datagrid-resource-search .form-control {
     background: var(--color-background, #ffffff);
     color: var(--color-text, #5c5d60);
     border-color: var(--vfx-resource-border);
     border-radius: var(--radius-sm, 8px);
     height: 40px;
     padding-right: var(--space-major-section-break);
}

.vfx-datagrid-resource-search .bi-search {
     color: var(--vfx-resource-muted);
     font-size: 18px;
     position: absolute;
     right: var(--space-card-padding);
     top: 50%;
     transform: translateY(-50%);
}

.vfx-datagrid-resource-filters {
     --vfx-filter-control-height: 40px;
     flex: 999 1 520px;
     min-width: min(100%, 320px);
}

.vfx-datagrid-resource-filters .search-container {
     margin-bottom: 0 !important;
     padding-left: 0 !important;
     padding-right: 0 !important;
}

.vfx-datagrid-resource-filters .search-container > .flex-fill > .d-flex {
     column-gap: var(--space-card-padding);
     row-gap: var(--space-form-field-gap);
}

.vfx-datagrid-resource-filters .search-container > .flex-fill > .d-flex > [class*="col-"] {
     min-width: 220px;
}

.vfx-datagrid-resource-filters .row,
.vfx-datagrid-resource-filters [class*="col-"] {
     margin-left: 0 !important;
}

.vfx-datagrid-resource-filters .mb-3 {
     margin-bottom: 0 !important;
}

.vfx-datagrid-resource-filters .align-items-start {
     display: block;
}

.vfx-datagrid-resource-filters .align-items-start > [class*="col-"] {
     max-width: 100%;
     padding-left: 0;
     padding-right: 0;
     width: 100%;
}

.vfx-datagrid-resource-filters label {
     color: var(--vfx-resource-muted);
     display: block;
     font-size: 13px;
     line-height: 1.25;
     margin-bottom: var(--space-tight-padding) !important;
     padding-top: 0 !important;
}

.vfx-datagrid-resource-filters .form-control:not(.form-check-input),
.vfx-datagrid-resource-filters .form-select {
     background: var(--color-background, #ffffff);
     color: var(--color-text, #5c5d60);
     min-height: 40px;
}

.vfx-datagrid-resource-filters .form-switch {
     display: grid;
     grid-template-rows: auto var(--vfx-filter-control-height);
     min-height: 0;
     padding-left: 0;
     row-gap: var(--space-tight-padding);
}

.vfx-datagrid-resource-filters .form-switch label {
     margin-bottom: 0 !important;
}

.vfx-datagrid-resource-filters .form-switch .form-check-input {
     align-self: center;
     border-radius: 999px;
     display: block;
     float: none;
     height: var(--space-card-padding) !important;
     margin-left: 0;
     margin-top: 0 !important;
     min-height: var(--space-card-padding) !important;
     padding: 0 !important;
     width: var(--space-between-cards) !important;
}

.vfx-datagrid-resource-filters .search-container .ml-auto {
     align-items: end;
     display: flex;
     gap: var(--space-tight-padding);
}

.vfx-datagrid-resource-filters .search-container .ml-auto .btn {
     align-items: center;
     display: inline-flex;
     height: 40px !important;
     justify-content: center;
     margin-bottom: 0 !important;
     margin-left: 0 !important;
}

.vfx-datagrid-resource-toolbar-action {
     align-items: center;
     background: var(--color-background, #ffffff);
     border: 1px solid var(--vfx-resource-border);
     border-radius: var(--radius-sm, 8px);
     color: var(--vfx-resource-primary);
     display: inline-flex;
     gap: var(--space-tight-padding);
     height: 40px;
     justify-content: center;
     padding: 0 var(--space-card-padding);
     transition: background-color 0.15s ease, border-color 0.15s ease;
}

.vfx-datagrid-resource-toolbar-action:hover {
     background: var(--color-background-light, #f8f9fa);
     border-color: var(--vfx-resource-primary);
}

.vfx-datagrid-resource-toolbar-action:focus-visible {
     outline: none;
     box-shadow: var(--shadow-button-focus, 0 0 0px 2px #fff, 0 0 3px 4px rgba(234, 133, 34, 0.5));
}

.vfx-datagrid-resource div.dt-container {
     border: 0;
     border-radius: 0;
     box-shadow: none;
     margin: 0;
     padding: 0;
}

.vfx-datagrid-resource div.dt-container > div {
     margin: 0;
}

.vfx-datagrid-resource div.dt-layout-full > div.dt-scroll {
     border-bottom: 0;
}

.vfx-datagrid-resource div.dt-layout-row:has(.dt-length),
.vfx-datagrid-resource div.dt-layout-row:has(.dt-info),
.vfx-datagrid-resource div.dt-layout-row:has(.dt-paging) {
     align-items: center;
     color: var(--vfx-resource-muted);
     display: flex;
     font-size: 13px;
     justify-content: space-between;
     padding: var(--space-card-padding);
}

.vfx-datagrid-resource table.dataTable.vfx-datagrid-resource-table {
     border-collapse: separate;
     border-spacing: 0;
     margin: 0 !important;
     width: 100% !important;
}

.vfx-datagrid-resource table.dataTable.vfx-datagrid-resource-table thead > tr > th,
.vfx-datagrid-resource table.dataTable.vfx-datagrid-resource-table tbody > tr > td {
     border-bottom: 1px solid var(--vfx-resource-border);
     color: var(--color-text-strong, #111827);
     font-size: 14px;
     padding: var(--space-card-padding);
     vertical-align: middle;
}

.vfx-datagrid-resource table.dataTable.vfx-datagrid-resource-table thead > tr > th {
     background: var(--vfx-resource-offwhite);
     color: var(--color-text-strong, #111827);
     font-size: 13px;
     font-weight: 700;
     padding: var(--space-card-padding);
}

.vfx-datagrid-resource table.dataTable.vfx-datagrid-resource-table > tbody > tr > * {
     box-shadow: none !important;
}

.vfx-datagrid-resource table.dataTable.vfx-datagrid-resource-table > tbody > tr:hover > td {
     background: var(--color-background-light, #f8f9fa);
}

.vfx-datagrid-resource-cell {
     align-items: center;
     display: grid;
     gap: var(--space-form-field-gap);
     grid-template-columns: 52px minmax(0, 1fr);
}

.vfx-datagrid-resource-thumb {
     align-items: center;
     border-radius: var(--space-tight-padding);
     display: flex;
     height: 52px;
     justify-content: center;
     overflow: hidden;
     width: 52px;
}

.vfx-datagrid-resource-thumb img {
     height: 100%;
     object-fit: cover;
     width: 100%;
}

.vfx-datagrid-resource-name {
     color: var(--vfx-resource-primary);
     font-size: 15px;
     font-weight: 700;
     line-height: 1.25;
     margin: 0;
}

.vfx-datagrid-resource-description {
     color: var(--color-text-secondary, #687087);
     font-size: 13px;
     line-height: 1.45;
     margin: var(--space-icon-gap) 0 0;
}

.vfx-datagrid-resource .expand {
     align-items: center;
     background: var(--color-background, #ffffff) !important;
     border: 1px solid var(--vfx-resource-border) !important;
     border-radius: var(--radius-sm, 8px) !important;
     color: var(--vfx-resource-primary) !important;
     display: inline-flex;
     font-size: 20px;
     height: 36px;
     justify-content: center;
     padding: 0;
     width: 36px;
     transition: background-color 0.15s ease;
}

.vfx-datagrid-resource .expand:hover {
     background: var(--color-background-light, #f8f9fa) !important;
}

.vfx-datagrid-resource .grid-action-button {
     align-items: center;
     background: var(--color-background, #ffffff) !important;
     border: 1px solid var(--vfx-resource-border) !important;
     border-radius: var(--radius-sm, 8px) !important;
     color: var(--vfx-resource-primary) !important;
     display: inline-flex;
     height: 36px;
     justify-content: center;
     margin: 0 !important;
     padding: 0 !important;
     width: 36px;
     transition: background-color 0.15s ease;
}

.vfx-datagrid-resource .grid-action-button-group {
     align-items: center;
     display: inline-flex;
     flex-wrap: nowrap;
     gap: var(--space-tight-padding);
     justify-content: flex-end;
}

.vfx-datagrid-resource .grid-action-button:hover {
     background: var(--color-background-light, #f8f9fa) !important;
     transform: none !important;
}

.vfx-datagrid-resource-child-header {
     align-items: center;
     display: flex;
     gap: var(--space-tight-padding);
     padding: var(--space-tight-padding) var(--space-card-padding);
}

.vfx-datagrid-resource-child-header span {
     background: var(--vfx-resource-primary);
     border-radius: 999px;
     height: var(--space-card-padding);
     opacity: 0.45;
     width: 3px;
}

.vfx-datagrid-resource-child-header strong {
     color: var(--vfx-resource-primary);
     font-size: 16px;
     font-weight: 700;
     line-height: 1.25;
}

.vfx-datagrid-resource-child table.dataTable.vfx-datagrid-resource-table thead > tr > th,
.vfx-datagrid-resource-child table.dataTable.vfx-datagrid-resource-table tbody > tr > td {
     font-size: 12px;
     padding: var(--space-tight-padding) var(--space-form-field-gap);
}

.vfx-datagrid-resource-child table.dataTable.vfx-datagrid-resource-table thead > tr > th {
     font-size: 12px;
     padding: var(--space-tight-padding) var(--space-form-field-gap);
}

.vfx-datagrid-resource-child .vfx-datagrid-resource-cell {
     gap: var(--space-tight-padding);
     grid-template-columns: 36px minmax(0, 1fr);
}

.vfx-datagrid-resource-child .vfx-datagrid-resource-thumb {
     height: 36px;
     width: 36px;
}

.vfx-datagrid-resource-child .vfx-datagrid-resource-name {
     font-size: 13px;
}

.vfx-datagrid-resource-child .vfx-datagrid-resource-description {
     font-size: 12px;
     line-height: 1.35;
}

.vfx-datagrid-resource-child .grid-action-button {
     height: 30px;
     width: 30px;
}

.vfx-datagrid-resource-footer-action {
     padding: var(--space-tight-padding) var(--space-card-padding);
}

.vfx-datagrid-resource-link-action {
     color: var(--vfx-resource-primary);
     font-weight: 600;
     padding: 0;
     text-decoration: none;
     transition: color 0.15s ease;
}

.vfx-datagrid-resource-link-action:hover,
.vfx-datagrid-resource-link-action:focus-visible {
     color: var(--color-accent-light, #ea8522);
     text-decoration: underline;
}
`;